// @flow
import React, { useState, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import deepEqual from 'fast-deep-equal';
import Box from "@material-ui/core/Box";
import List from "@material-ui/core/List";
import { Alert, User, Zone } from '../../types';
import Header from '../Sidebar/Header';
import { types as messages } from './messages';
import SearchForm from './SearchForm';
import AlertList from './AlertList';

const noResultsMessage = (
    <FormattedMessage
        id="alerts.nothingFound"
        defaultMessage="Nothing found"
    />
);

export type Props = $ReadOnly<{
    list: $ReadOnlyArray<string>,
    entities: $ReadOnly<{[id: string]: Alert}>,
    callsigns: $ReadOnly<{[icao: string]: string}>,
    userEntities: $ReadOnly<{[userId: string]: User}>,
    zoneEntities: $ReadOnly<{[userId: string]: Zone}>
}>;

function Alerts({ list, entities, callsigns, userEntities, zoneEntities }: Props) {
    const intl = useIntl();
    const [searchQuery, setSearchQuery] = useState();
    const callsignsRef = useRef();
    const alertDataRef = useRef();

    if(!callsignsRef.current || !deepEqual(callsignsRef.current, callsigns)) {
        callsignsRef.current = callsigns;
    }

    const alertData = useMemo(() => {
        const output = {};
        for(let i = 0, length = list.length; i < length; i += 1) {
            const alertId = list[i];
            const alert = entities[alertId];

            if(!alert) {
                continue;
            }

            let callsign;
            if(alert.icao && callsignsRef.current[alert.icao]) {
                callsign = callsignsRef.current[alert.icao];
            }

            let user;
            if(alert.user) {
                const userEntitiy = userEntities[alert.user];

                if(userEntitiy) {
                    user = userEntitiy.name;
                }
            }

            let zone;
            if(alert.meta) {
                if(alert.meta.zoneId) {
                    const zoneEntity = zoneEntities[alert.meta.zoneId];

                    if(zoneEntity) {
                        zone = zoneEntity.name;
                    }
                }
            }

            const messageDescriptor = messages[alert.type];
            const message = messageDescriptor && intl.formatMessage(messageDescriptor, {
                ...alert.meta,
                user,
                zone
            });

            const data = {
                type: alert.type,
                meta: alert.meta,
                date: alert.date && alert.date.toDate().toLocaleString(),
                seen: Boolean(alert.seenDate),
                message,
                callsign,
                user,
                zone
            };

            const oldData = alertDataRef.current && alertDataRef.current[alertId];

            if(oldData && deepEqual(data, oldData)) {
                output[alertId] = oldData;
            }
            else {
                output[alertId] = data;
            }
        }

        alertDataRef.current = output;

        return output;
    }, [intl, list, entities, callsignsRef.current, userEntities, zoneEntities]);

    const handleSearch = (query) => setSearchQuery(query);

    const filteredList = useMemo(() => {
        if(list.length === 0) {
            return null;
        }

        if(!searchQuery || list.length === 0) {
            return null;
        }

        let filteredList = list;
        if(searchQuery) {
            filteredList = list.filter(alertId => {
                const alert = alertData[alertId];

                if(!alert) {
                    return false;
                }

                const ciQuery = searchQuery.toLowerCase();

                if(alert.message && alert.message.toLowerCase().indexOf(ciQuery) !== -1) {
                    return true;
                }
                if(alert.callsign && alert.callsign.toLowerCase().indexOf(ciQuery) !== -1) {
                    return true;
                }
                if(alert.user && alert.user.toLowerCase().indexOf(ciQuery) !== -1) {
                    return true;
                }
                if(alert.zone && alert.zone.toLowerCase().indexOf(ciQuery) !== -1) {
                    return true;
                }

                return false;
            });
        }

        return filteredList.sort((a, b) => {
            const aTimestamp = entities[a].date.toMillis();
            const bTimestamp = entities[b].date.toMillis();

            if(typeof aTimestamp === 'undefined' || bTimestamp === 'undefined') {
                return 0;
            }

            if(aTimestamp > bTimestamp) {
                return -1;
            }
            else if(bTimestamp > aTimestamp) {
                return 1;
            }
            else {
                return 0;
            }
        });
    }, [list, entities, alertData, searchQuery]);

    return (
        <List subheader={(
            <Header
                title={(
                    <Box fontSize="h6.fontSize">
                        <FormattedMessage
                            id="sidebar.alerts.header"
                            defaultMessage="Alerts"
                        />
                    </Box>
                )}
            />
        )}>
            <Box mx={2} mt={2} pb={5}>
                <SearchForm onChange={handleSearch} />
                <AlertList list={filteredList || list} alertData={alertData} emptyMessage={filteredList ? noResultsMessage : undefined} />
            </Box>
        </List>
    );
}

export default Alerts;